import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import useBoolean from "hooks/useBoolean";
import { useEffect, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import translateError from "utils/translateError";

const styles = {
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
};

type ChangeReasonDialogProps = {
  open: boolean;
  changeReason: string;
  setChangeReason: (reason: string) => void;
  onCancel: () => void;
  handleConfirm: () => Promise<void>;
  titleKey?: string;
};

const ChangeReasonDialog = ({
  open,
  changeReason,
  setChangeReason,
  onCancel,
  handleConfirm,
  titleKey = "confirmOperation",
}: ChangeReasonDialogProps) => {
  const [confirming, confirmingActions] = useBoolean(false);

  const [error, setError] = useState<string>();
  const { formatMessage: t } = useIntl();

  useEffect(() => {
    if (!open) {
      setChangeReason("");
      setError(undefined);
    }
  }, [open, setChangeReason]);

  const confirm = async () => {
    try {
      setError(undefined);
      if (!changeReason) {
        setError(t({ id: "changeReasonRequired" }));
        return;
      }
      confirmingActions.setTrue();
      await handleConfirm();
    } catch (error) {
      setError(translateError(error, t));
    } finally {
      confirmingActions.setFalse();
    }
  };

  return (
    <Dialog open={open} disableEnforceFocus>
      <DialogTitle>
        <T id={titleKey} />
      </DialogTitle>
      <DialogContent dividers>
        <Typography paragraph>{t({ id: "areYouSureText" })}</Typography>
        <Typography paragraph>
          {t({ id: "changeReasonRequestText" })}
        </Typography>

        <TextField
          variant="standard"
          fullWidth
          disabled={confirming}
          placeholder={t({ id: "changeReasonLabel" })}
          value={changeReason}
          onChange={(e) => {
            setChangeReason(e.target.value);
            setError(undefined);
          }}
        />
        {error && (
          <Alert sx={styles.feedback} severity="error">
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {confirming ? <CircularProgress size={16} /> : null}
        <Button
          autoFocus
          onClick={onCancel}
          color="primary"
          disabled={confirming}
        >
          <T id="no" />
        </Button>
        <Button onClick={confirm} color="primary" disabled={confirming}>
          <T id="yes" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeReasonDialog;
